export interface YardZoneGuidanceInput {
  name?: string | null;
  resourceCode?: string | null;
  resourceType?: string | null;
  category?: string | null;
}

export interface YardZoneGuidance {
  zoneCode: string;
  zoneName: string;
  reason: string;
}

const CATEGORY_ZONE_DEFAULTS: Record<string, { zoneCode: string; zoneName: string }> = {
  'aggregates & fill': { zoneCode: 'Z1', zoneName: 'Aggregates & fill laydown' },
  siteworks: { zoneCode: 'Z2', zoneName: 'Siteworks / road furniture / fencing' },
  'road furniture & markings': { zoneCode: 'Z2', zoneName: 'Siteworks / road furniture / fencing' },
  fencing: { zoneCode: 'Z2', zoneName: 'Siteworks / road furniture / fencing' },
  'reinforcement steel': { zoneCode: 'Z3b', zoneName: 'Reinforcement steel storage' },
  'carpentry & joinery': { zoneCode: 'Z4', zoneName: 'Carpentry and joinery storage' },
  'carpentry mouldings & profiles': { zoneCode: 'Z4', zoneName: 'Carpentry and joinery storage' },
  roofing: { zoneCode: 'Z5', zoneName: 'Roofing storage' },
  concrete: { zoneCode: 'Z6', zoneName: 'Concrete / plaster / treatment storage' },
  'plaster & render': { zoneCode: 'Z6', zoneName: 'Concrete / plaster / treatment storage' },
  waterproofing: { zoneCode: 'Z6', zoneName: 'Concrete / plaster / treatment storage' },
  'termite treatment': { zoneCode: 'Z6', zoneName: 'Concrete / plaster / treatment storage' },
  'adhesives & sealants': { zoneCode: 'Z6', zoneName: 'Concrete / plaster / treatment storage' },
  'drywall partition': { zoneCode: 'Z7', zoneName: 'Drywall / tiling storage' },
  tiling: { zoneCode: 'Z7', zoneName: 'Drywall / tiling storage' },
  fixings: { zoneCode: 'Z8', zoneName: 'General building / fixings / consumables' },
  cleaning: { zoneCode: 'Z8', zoneName: 'General building / fixings / consumables' },
  'general building': { zoneCode: 'Z8', zoneName: 'General building / fixings / consumables' },
  'provisional materials': { zoneCode: 'Z8', zoneName: 'General building / fixings / consumables' },
  'consumables (sundry)': { zoneCode: 'Z8', zoneName: 'General building / fixings / consumables' },
  plumbing: { zoneCode: 'Z9', zoneName: 'Plumbing / electrical / PV storage' },
  electrical: { zoneCode: 'Z9', zoneName: 'Plumbing / electrical / PV storage' },
  pv: { zoneCode: 'Z9', zoneName: 'Plumbing / electrical / PV storage' },
  'doors & windows (units)': { zoneCode: 'Z10', zoneName: 'Doors and windows storage' },
  paint: { zoneCode: 'Z11b', zoneName: 'Paint store' },
  formwork: { zoneCode: 'A3a', zoneName: 'Formwork operational area' },
  survey: { zoneCode: 'Z12', zoneName: 'Tool crib / survey storage' },
};

const TYPE_ZONE_DEFAULTS: Record<string, { zoneCode: string; zoneName: string }> = {
  tool: { zoneCode: 'Z12', zoneName: 'Tool crib / survey storage' },
  equipment: { zoneCode: 'Z13', zoneName: 'Plant and equipment yard' },
  supply: { zoneCode: 'Z8', zoneName: 'General building / fixings / consumables' },
};

const normalize = (value?: string | null): string => String(value || '').trim().toLowerCase();

const baseCategory = (category?: string | null): string => normalize(category).split('/')[0].trim();

const inferCategoryFromText = (input: YardZoneGuidanceInput): string => {
  const text = normalize(`${input.resourceCode || ''} ${input.name || ''}`);
  if (/\brebar\b|reinforc|steel/.test(text)) return 'reinforcement steel';
  if (/formwork|shutter|plywood/.test(text)) return 'formwork';
  if (/cement|concrete|mortar|grout|plaster|render/.test(text)) return 'concrete';
  if (/pipe|plumb|pvc|valve|fitting/.test(text)) return 'plumbing';
  if (/electrical|cable|wire|conduit|breaker|pv|solar/.test(text)) return 'electrical';
  if (/tile|tiling/.test(text)) return 'tiling';
  if (/paint|primer/.test(text)) return 'paint';
  if (/door|window/.test(text)) return 'doors & windows (units)';
  if (/roof/.test(text)) return 'roofing';
  if (/aggregate|sand|stone|fill|base course/.test(text)) return 'aggregates & fill';
  if (/tool|survey|level|total station/.test(text)) return 'survey';
  return '';
};

export const yardZoneGuidanceForResource = (input: YardZoneGuidanceInput): YardZoneGuidance | null => {
  const category = baseCategory(input.category) || inferCategoryFromText(input);
  const categoryDefault = CATEGORY_ZONE_DEFAULTS[category];
  if (categoryDefault) {
    return { ...categoryDefault, reason: category || 'material category' };
  }

  const typeDefault = TYPE_ZONE_DEFAULTS[normalize(input.resourceType)];
  if (typeDefault) {
    return { ...typeDefault, reason: `${input.resourceType} fallback` };
  }

  return null;
};

export const yardZoneLabel = (guidance: YardZoneGuidance | null): string =>
  guidance ? `${guidance.zoneCode} - ${guidance.zoneName}` : 'No yard-zone default yet';
